// Product Control Plane — one product: live state, draft state, revision
// history, and rollback.
//
// LIVE and DRAFT are the two facts an operator must never mistake for each
// other, so they are two bands of equal weight at the top of the page rather
// than a badge somewhere. Everything below them is history.

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ConfirmDialog } from "../components/ConfirmDialog";
import {
  bootstrap,
  getProduct,
  getRevision,
  listRevisions,
  rollback,
  type ProductDetailResponse,
  type RevisionsResponse,
} from "../controlPlane/api";
import {
  allItems,
  decideItemVisibility,
  type ControlPlaneDocument,
  type Lane,
} from "../controlPlane/contract";
import { diffDocuments, summarize } from "../controlPlane/diff";
import { laneHost, laneLabel, useControlPlaneTheme } from "../controlPlane/theme";
import { ActionCard, ActionRow, WorkflowStepper } from "../ui/Workflow";
import { IconEdit, IconHistory, IconPublish, IconRollback } from "../ui/icons";
import {
  AuthorityNotice,
  GAOSAFE_APPROVAL_LABEL,
  GAOSAFE_APPROVAL_REASON,
  useWritesBlocked,
} from "../authority";
import {
  Btn,
  Card,
  CpHeader,
  Empty,
  ErrorNote,
  Hash,
  Kv,
  KvList,
  LaneChip,
  StatusChip,
} from "../controlPlane/ui";
import { tdStyle, thStyle } from "../controlPlane/styles";

function describeError(e: unknown): string {
  return e instanceof Error ? e.message : String(e);
}

export function ControlPlaneProduct() {
  const { productId = "" } = useParams();
  const { themeProps, toggle, theme } = useControlPlaneTheme();

  const [detail, setDetail] = useState<ProductDetailResponse | null>(null);
  const [revisions, setRevisions] = useState<RevisionsResponse | null>(null);
  const historyRef = useRef<HTMLDivElement | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [notice, setNotice] = useState<string | null>(null);

  // Rollback needs the TARGET document before it can preview anything, so the
  // dialog is opened only once that fetch resolves. A rollback confirmation
  // that cannot say what will change is not a confirmation.
  const [rollbackTarget, setRollbackTarget] = useState<{
    revisionId: string;
    document: ControlPlaneDocument;
  } | null>(null);
  const [rollbackBusy, setRollbackBusy] = useState(false);
  const [rollbackError, setRollbackError] = useState<string | null>(null);
  const [bootstrapOpen, setBootstrapOpen] = useState(false);
  const [bootstrapBusy, setBootstrapBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [d, r] = await Promise.all([
        getProduct(productId),
        listRevisions(productId).catch(() => null),
      ]);
      setDetail(d);
      setRevisions(r);
    } catch (e) {
      setError(describeError(e));
    } finally {
      setLoading(false);
    }
  }, [productId]);

  useEffect(() => {
    void load();
  }, [load]);

  const lane: Lane | null = detail?.lane ?? null;
  const published = detail?.published ?? null;

  const visibleCount = useMemo(() => {
    if (!published) return null;
    const items = allItems(published.document);
    // Probe with a very high version so the count reports lane visibility
    // rather than "hidden from a client that declared nothing", which is true
    // of every version-gated row and tells an operator nothing.
    if (lane === null) return { visible: null, total: items.length };
    const visible = items.filter(
      (i) => decideItemVisibility(i, lane, "999999.0.0").visible,
    ).length;
    return { visible, total: items.length };
  }, [published, lane]);

  const rollbackDiff = useMemo(() => {
    if (!rollbackTarget || !published) return null;
    return diffDocuments(
      published.document,
      rollbackTarget.document,
      lane === null ? undefined : [lane],
    );
  }, [rollbackTarget, published, lane]);

  async function openRollback(revisionId: string) {
    setRollbackError(null);
    try {
      const rev = await getRevision(productId, revisionId);
      setRollbackTarget({ revisionId, document: rev.document });
    } catch (e) {
      setError(describeError(e));
    }
  }

  async function doRollback() {
    if (!rollbackTarget) return;
    setRollbackBusy(true);
    setRollbackError(null);
    try {
      const r = await rollback(productId, rollbackTarget.revisionId);
      setRollbackTarget(null);
      setNotice(
        `Live pointer moved ${r.fromRevisionId} → ${r.toRevisionId}. Neither revision was rewritten.`,
      );
      await load();
    } catch (e) {
      setRollbackError(describeError(e));
    } finally {
      setRollbackBusy(false);
    }
  }

  async function doBootstrap() {
    setBootstrapBusy(true);
    try {
      const r = await bootstrap(productId);
      const dropped = r.migrated.dropped;
      setBootstrapOpen(false);
      setNotice(
        `Published ${r.revisionId} from ${r.migrated.carriedCount} of ${r.migrated.sourceRowCount} legacy rows.` +
          (dropped.length > 0
            ? ` ${dropped.length} row(s) could not be carried: ${dropped
                .map((d) => `${d.id} (${d.reason})`)
                .join("; ")}`
            : " No rows were dropped."),
      );
      await load();
    } catch (e) {
      setError(describeError(e));
      setBootstrapOpen(false);
    } finally {
      setBootstrapBusy(false);
    }
  }

  const hasDraftChanges = detail?.draft ? !detail.draft.matchesPublished : false;
  const writesBlocked = useWritesBlocked();

  return (
    <div {...themeProps} style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <CpHeader
        breadcrumb={<Link to="/control-plane">Control Plane</Link>}
        title={productId}
        subtitle={detail?.product.displayName}
        actions={
          <>
            <LaneChip lane={lane} host={laneHost(lane)} />
            <button type="button" onClick={toggle}>
              {theme === "light" ? "Dark" : "Light"}
            </button>
            <button type="button" onClick={() => void load()} disabled={loading}>
              {loading ? "Loading…" : "Refresh"}
            </button>
          </>
        }
      />

      {error !== null && <ErrorNote>{error}</ErrorNote>}
      {notice !== null && (
        <Card accent="ok">
          <div style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
            <div style={{ flex: 1 }}>{notice}</div>
            <Btn onClick={() => setNotice(null)}>Dismiss</Btn>
          </div>
        </Card>
      )}

      {/* ── Actions first ──────────────────────────────────────────────
          The four verbs an operator opens this page to perform. They were
          previously three small links buried inside the Draft card and one
          button per row at the bottom of a revision table, so "what can I do
          here" was answered by scanning the whole page.

          Every card maps to a real endpoint (or to the route that owns it):
          EDIT CONTENT → the draft editor, PUBLISH → the review screen which
          holds the diff, the validation report and the typed confirmation,
          ROLLBACK → the revision history where a target must be chosen, and
          VIEW HISTORY → the audit trail. PUBLISH and ROLLBACK deliberately
          NAVIGATE rather than act: neither is safe as a one-click card,
          because publish needs an expectedBaseRevisionId and a diff the
          operator has actually seen, and rollback has no meaning without a
          chosen revision. */}
      {writesBlocked && <AuthorityNotice scope="Control Plane writes are blocked." />}

      {detail && (
        <>
          <ActionRow>
            <ActionCard
              label="Edit content"
              hint={
                detail.draft
                  ? hasDraftChanges
                    ? "Draft has pending changes"
                    : "Draft is in step with live"
                  : "Start a draft from the live revision"
              }
              icon={<IconEdit size={18} />}
              to={writesBlocked ? undefined : `/control-plane/${productId}/edit`}
              disabled={writesBlocked}
              disabledReason={GAOSAFE_APPROVAL_LABEL}
            />
            <ActionCard
              label="Publish"
              tone={hasDraftChanges ? "accent" : "neutral"}
              icon={<IconPublish size={18} />}
              to={writesBlocked ? undefined : `/control-plane/${productId}/review`}
              hint={hasDraftChanges ? "Review the diff, then publish" : "Review changes"}
              disabled={writesBlocked || !hasDraftChanges}
              disabledReason={
                writesBlocked
                  ? GAOSAFE_APPROVAL_LABEL
                  : "No unpublished changes to publish"
              }
            />
            <ActionCard
              label="Roll back"
              tone="danger"
              icon={<IconRollback size={18} />}
              onClick={() => {
                historyRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
              }}
              hint="Choose a revision to restore"
              disabled={writesBlocked || (revisions?.revisions.length ?? 0) < 2}
              disabledReason={
                writesBlocked
                  ? GAOSAFE_APPROVAL_LABEL
                  : "Needs a second revision to roll back to"
              }
            />
            <ActionCard
              label="View history"
              icon={<IconHistory size={18} />}
              to={`/control-plane/${productId}/audit`}
              hint="Publication and rollback trail"
            />
          </ActionRow>

          {/* The stepper narrates where the draft stands, which stays useful
              while writes are frozen. What it must NOT do is hand the operator
              a link into the publish workflow that the action cards directly
              above have just refused — one screen saying both "you cannot start
              this" and "click here to start it". Under the freeze every step
              keeps its state and loses its link. */}
          <WorkflowStepper
            label="Control Plane publish workflow"
            steps={[
              {
                key: "draft",
                label: "Draft",
                to: writesBlocked ? undefined : `/control-plane/${productId}/edit`,
                state: hasDraftChanges ? "done" : "available",
                hint: hasDraftChanges ? "Changes pending" : "No changes",
              },
              {
                key: "validate",
                label: "Validate",
                to:
                  hasDraftChanges && !writesBlocked
                    ? `/control-plane/${productId}/review`
                    : undefined,
                // Never "done": `validateDraft` result is not persisted
                // anywhere, so this page cannot know whether it ever ran.
                state: hasDraftChanges ? "available" : "blocked",
                hint: hasDraftChanges ? "Runs on review" : undefined,
              },
              {
                key: "review",
                label: "Review",
                to:
                  hasDraftChanges && !writesBlocked
                    ? `/control-plane/${productId}/review`
                    : undefined,
                state: hasDraftChanges ? "current" : "blocked",
                hint: hasDraftChanges ? "Diff against live" : undefined,
              },
              {
                key: "publish",
                label: "Publish",
                to:
                  hasDraftChanges && !writesBlocked
                    ? `/control-plane/${productId}/review`
                    : undefined,
                state: hasDraftChanges ? "available" : published !== null ? "done" : "blocked",
                hint: writesBlocked
                  ? GAOSAFE_APPROVAL_LABEL
                  : published !== null && !hasDraftChanges
                    ? `Live: ${published.revisionId}`
                    : undefined,
              },
            ]}
          />
        </>
      )}

      {detail && published === null && detail.current === null && (
        <Card accent="warn" title="No revision has ever been published">
          <p style={{ margin: "0 0 10px" }}>
            Until a first revision exists, <code>/v2/explorer/sites</code> is
            served from the legacy <code>explorer_sites</code> table, so no
            installed app is affected. Bootstrapping reads that table exactly as
            it stands, hashes the real bytes, and publishes them as{" "}
            <code>rev_000001</code>. Rows it cannot carry are reported, never
            dropped silently.
          </p>
          <Btn
            tone="primary"
            onClick={() => setBootstrapOpen(true)}
            disabled={writesBlocked}
            title={writesBlocked ? GAOSAFE_APPROVAL_REASON : undefined}
          >
            Bootstrap from explorer_sites…
          </Btn>
        </Card>
      )}

      {detail?.publishedError && published === null && detail.current !== null && (
        <ErrorNote>
          The live revision could not be read: <code>{detail.publishedError}</code>.
          Consumers are being served a fail-closed 503 rather than a document
          this service cannot vouch for.
        </ErrorNote>
      )}

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))",
          gap: 14,
        }}
      >
        <Card accent="ok" title="Live — what devices render now">
          {published === null ? (
            <Empty>Nothing published.</Empty>
          ) : (
            <>
              <div style={{ fontSize: 22, margin: "0 0 10px" }}>
                {published.revisionId}
              </div>
              <KvList>
                <Kv k="published">{published.publishedAt}</Kv>
                <Kv k="by">
                  {published.publishedBy}
                  {published.publishedByLabel && (
                    <span style={{ color: "var(--fg-dim)" }}>
                      {" "}
                      {published.publishedByLabel}
                    </span>
                  )}
                </Kv>
                <Kv k="hash">
                  <Hash value={published.documentHash} />
                </Kv>
                <Kv k="pointer">
                  moved {detail?.current?.pointerSeq ?? 1}×
                </Kv>
                {visibleCount && (
                  <Kv k="rows">
                    {visibleCount.visible === null
                      ? `${visibleCount.total} — lane unknown, so render count is unknown`
                      : `${visibleCount.visible} of ${visibleCount.total} render on this lane`}
                  </Kv>
                )}
              </KvList>
            </>
          )}
        </Card>

        <Card
          accent={hasDraftChanges ? "warn" : undefined}
          title={
            hasDraftChanges
              ? "Draft — not visible to anyone"
              : "Draft — no unpublished changes"
          }
        >
          {detail?.draft ? (
            <>
              <div
                style={{
                  fontSize: 22,
                  margin: "0 0 10px",
                  color: hasDraftChanges ? "var(--warn)" : "var(--fg-dim)",
                }}
              >
                {hasDraftChanges ? "Changes pending" : "In step with live"}
              </div>
              <KvList>
                <Kv k="edited">{detail.draft.updatedAt}</Kv>
                <Kv k="by">{detail.draft.updatedBy}</Kv>
                <Kv k="based on">{detail.draft.baseRevisionId ?? "—"}</Kv>
                <Kv k="hash">
                  <Hash value={detail.draft.documentHash} />
                </Kv>
              </KvList>
              <div style={{ display: "flex", gap: 8, marginTop: 14, flexWrap: "wrap" }}>
                {writesBlocked ? (
                  <Btn disabled title={GAOSAFE_APPROVAL_REASON}>
                    Edit rows
                  </Btn>
                ) : (
                  <Link to={`/control-plane/${productId}/edit`}>
                    <Btn>Edit rows</Btn>
                  </Link>
                )}
                {writesBlocked ? (
                  <Btn disabled title={GAOSAFE_APPROVAL_REASON}>
                    Review &amp; publish…
                  </Btn>
                ) : (
                  <Link to={`/control-plane/${productId}/review`}>
                    <Btn tone={hasDraftChanges ? "primary" : "default"}>
                      Review &amp; publish…
                    </Btn>
                  </Link>
                )}
                <Link to={`/control-plane/${productId}/audit`}>
                  <Btn>Audit</Btn>
                </Link>
              </div>
            </>
          ) : (
            <Empty>No draft.</Empty>
          )}
        </Card>
      </div>

      {/* The Roll back action card scrolls here rather than acting: a
          rollback has no meaning without a chosen target revision, so the
          card's job is to put the operator in front of the list that has
          them. The per-row "Roll back to…" buttons still own the action. */}
      <div ref={historyRef} />
      <Card
        title="Revision history"
        note="immutable — rollback moves the live pointer, nothing is rewritten"
        style={{ padding: 0 }}
      >
        {!revisions || revisions.revisions.length === 0 ? (
          <Empty>No revisions.</Empty>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr>
                  <th style={thStyle}>State</th>
                  <th style={thStyle}>Revision</th>
                  <th style={thStyle}>Published</th>
                  <th style={thStyle}>By</th>
                  <th style={thStyle}>Hash</th>
                  <th style={thStyle} />
                </tr>
              </thead>
              <tbody>
                {revisions.revisions.map((r) => (
                  <tr key={r.revisionId}>
                    <td style={tdStyle}>
                      {r.isCurrent ? (
                        <StatusChip tone="ok">LIVE</StatusChip>
                      ) : (
                        <span style={{ color: "var(--fg-dim)" }}>—</span>
                      )}
                    </td>
                    <td style={tdStyle}>{r.revisionId}</td>
                    <td style={{ ...tdStyle, color: "var(--fg-dim)" }}>
                      {r.publishedAt}
                    </td>
                    <td style={{ ...tdStyle, color: "var(--fg-dim)" }}>
                      {r.publishedBy}
                    </td>
                    <td style={tdStyle}>
                      <Hash value={r.documentHash} />
                    </td>
                    <td style={{ ...tdStyle, textAlign: "right" }}>
                      {r.isCurrent ? null : (
                        <Btn
                          onClick={() => void openRollback(r.revisionId)}
                          disabled={writesBlocked}
                          title={writesBlocked ? GAOSAFE_APPROVAL_REASON : undefined}
                        >
                          Roll back to…
                        </Btn>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* ── Rollback: preview first, confirm second ─────────────────────── */}
      <ConfirmDialog
        open={rollbackTarget !== null}
        title={`Roll back to ${rollbackTarget?.revisionId ?? ""}`}
        tone="warning"
        busy={rollbackBusy}
        error={rollbackError}
        description={
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            <div>
              This moves the live pointer only.{" "}
              <b>No revision is deleted or rewritten</b>, and the revision being
              rolled away from stays in the history as having been live. A
              publish after this creates the next number — it never reuses one.
            </div>
            {rollbackDiff && (
              <div
                style={{
                  border: "1px solid var(--border-faint, #20262e)",
                  borderRadius: 4,
                  padding: 10,
                  background: "var(--bg-faint, #11151a)",
                }}
              >
                <div style={{ marginBottom: 6 }}>
                  What devices on <b>{laneLabel(lane)}</b> will see:{" "}
                  {summarize(rollbackDiff)}
                </div>
                {rollbackDiff.visibility.length === 0 ? (
                  <div style={{ color: "var(--fg-dim)" }}>
                    No row changes whether it renders on this lane.
                  </div>
                ) : (
                  <ul style={{ margin: 0, paddingLeft: 18 }}>
                    {rollbackDiff.visibility.map((v) => (
                      <li key={`${v.itemId}-${v.lane}`}>
                        <b>{v.itemId}</b>{" "}
                        {v.isVisible ? (
                          <span style={{ color: "var(--ok)" }}>returns</span>
                        ) : (
                          <span style={{ color: "var(--danger)" }}>
                            disappears
                            {v.reason ? ` — ${v.reason}` : ""}
                          </span>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </div>
        }
        rows={[
          { key: "product", value: productId },
          { key: "lane", value: laneLabel(lane) },
          { key: "from", value: published?.revisionId ?? "—" },
          { key: "to", value: rollbackTarget?.revisionId ?? "—" },
        ]}
        requireTypedConfirmation={
          lane === "production"
            ? {
                expected: productId,
                label: `Type "${productId}" to confirm a PRODUCT rollback`,
              }
            : undefined
        }
        confirmLabel={`Roll back to ${rollbackTarget?.revisionId ?? ""}`}
        onConfirm={() => void doRollback()}
        onCancel={() => setRollbackTarget(null)}
      />

      <ConfirmDialog
        open={bootstrapOpen}
        title="Bootstrap the first revision"
        tone="normal"
        busy={bootstrapBusy}
        description={
          <>
            Reads <code>explorer_sites</code> as it stands right now — including
            disabled rows, so a retired site stays re-enableable — and publishes
            it as <code>rev_000001</code>. Refused if any revision already
            exists.
          </>
        }
        rows={[
          { key: "product", value: productId },
          { key: "lane", value: laneLabel(lane) },
        ]}
        confirmLabel="Bootstrap rev_000001"
        onConfirm={() => void doBootstrap()}
        onCancel={() => setBootstrapOpen(false)}
      />
    </div>
  );
}
